import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
	Alert,
	Box,
	Button,
	Card,
	CardActions,
	CardContent,
	CircularProgress,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	Typography,
} from '@mui/material'
import {
	excludeFromAvitoAutoload,
	fetchAvitoAutoloadFeed,
	getAvitoAutoload,
	getAvitoAutoloadErrorMessage,
	previewAvitoAutoloadFeed,
	saveAvitoFeedResponse,
} from '../../utils/avitoAutoload'
import { listAvitoAccounts } from '../../utils/avitoAccounts'
import { presentAvitoDiagnostics } from '../../utils/avitoDiagnostics'
import {
	createAvitoHostedFeed,
	getAvitoHostedFeed,
	getAvitoHostedFeedDiagnostics,
	getAvitoHostedFeedErrorMessage,
	rebuildAvitoHostedFeed,
	rotateAvitoHostedFeedToken,
} from '../../utils/avitoHostedFeed'
import { deriveAvitoAutoloadViewModel } from '../../utils/avitoAutoloadViewModel'
import AvitoAutoloadAddDialog from './AvitoAutoloadAddDialog'
import AvitoAutoloadGuideDialog from './AvitoAutoloadGuideDialog'
import AvitoAutoloadSettingsDialog from './AvitoAutoloadSettingsDialog'

function firstUserDiagnosticMessage(diagnostics, mode = 'included') {
	const presented = presentAvitoDiagnostics(diagnostics, { mode })
	return (
		presented.missing[0]?.message ||
		presented.clarify[0]?.message ||
		presented.technical[0]?.message ||
		'Требуется повторная проверка объявления.'
	)
}

function formatDateTime(value) {
	if (!value) return null
	return new Intl.DateTimeFormat('ru-RU', {
		dateStyle: 'medium',
		timeStyle: 'short',
	}).format(new Date(value))
}

function AvitoAutoloadWorkspace({ onManageAccounts }) {
	const [accounts, setAccounts] = useState([])
	const [accountId, setAccountId] = useState('')
	const [autoload, setAutoload] = useState({ count: 0, listings: [] })
	const [hostedFeed, setHostedFeed] = useState(null)
	const [hostedDiagnostics, setHostedDiagnostics] = useState(null)
	const [feedPreview, setFeedPreview] = useState(null)
	const [oldUrlWarning, setOldUrlWarning] = useState('')
	const [loading, setLoading] = useState(true)
	const [busy, setBusy] = useState(false)
	const [syncing, setSyncing] = useState(false)
	const [addOpen, setAddOpen] = useState(false)
	const [settingsOpen, setSettingsOpen] = useState(false)
	const [guideOpen, setGuideOpen] = useState(false)
	const [showConnectionStep, setShowConnectionStep] = useState(false)
	const [error, setError] = useState('')
	const hostedFeedRequestVersion = useRef(0)

	const selectedAccount = accounts.find(
		(account) => String(account.id) === String(accountId)
	)
	const viewModel = useMemo(
		() =>
			deriveAvitoAutoloadViewModel({
				accounts,
				selectedAccount,
				autoload,
				hostedFeed,
				syncing,
				showConnectionStep,
			}),
		[accounts, autoload, hostedFeed, selectedAccount, showConnectionStep, syncing]
	)
	const statusSecondaryAction =
		viewModel.secondaryAction?.id === 'open-settings'
			? null
			: viewModel.secondaryAction

	const loadAccounts = useCallback(async () => {
		setLoading(true)
		setError('')
		try {
			const loaded = await listAvitoAccounts()
			setAccounts(loaded)
			setAccountId((current) => {
				if (loaded.some((account) => String(account.id) === String(current))) {
					return current
				}
				return (
					loaded.find((account) => account.status === 'active')?.id ||
					loaded[0]?.id ||
					''
				)
			})
		} catch (requestError) {
			setError(getAvitoAutoloadErrorMessage(requestError))
		} finally {
			setLoading(false)
		}
	}, [])

	const loadAutoload = useCallback(async () => {
		const requestVersion = ++hostedFeedRequestVersion.current
		if (!accountId) {
			setAutoload({ count: 0, listings: [] })
			setHostedFeed(null)
			return
		}
		setLoading(true)
		setError('')
		setFeedPreview(null)
		setHostedDiagnostics(null)
		try {
			const [autoloadResult, hostedResult] = await Promise.all([
				getAvitoAutoload(accountId),
				getAvitoHostedFeed(accountId),
			])
			if (requestVersion !== hostedFeedRequestVersion.current) return
			setAutoload(autoloadResult)
			setHostedFeed(hostedResult)
		} catch (requestError) {
			if (requestVersion !== hostedFeedRequestVersion.current) return
			setError(getAvitoAutoloadErrorMessage(requestError))
		} finally {
			if (requestVersion === hostedFeedRequestVersion.current) setLoading(false)
		}
	}, [accountId])

	useEffect(() => {
		loadAccounts()
	}, [loadAccounts])

	useEffect(() => {
		setShowConnectionStep(false)
		setOldUrlWarning('')
		loadAutoload()
	}, [loadAutoload])

	const handlePrepareOrRetry = async () => {
		setSyncing(true)
		setBusy(true)
		setError('')
		setHostedDiagnostics(null)
		try {
			if (!hostedFeed?.configured) {
				setHostedFeed(await createAvitoHostedFeed(accountId))
			}
			const result = await rebuildAvitoHostedFeed(accountId)
			setHostedFeed(result)
			if (result.build?.success === false) {
				setHostedDiagnostics({
					diagnostics: result.build.diagnostics,
					blockingListings: result.build.blockingListings,
				})
			} else {
				setShowConnectionStep(!hostedFeed?.ready)
			}
		} catch (requestError) {
			const failedResult = requestError?.response?.data
			if (failedResult?.configured !== undefined) setHostedFeed(failedResult)
			if (failedResult?.build) {
				setHostedDiagnostics({
					diagnostics: failedResult.build.diagnostics,
					blockingListings: failedResult.build.blockingListings,
				})
			} else {
				setError(getAvitoHostedFeedErrorMessage(requestError))
			}
		} finally {
			setSyncing(false)
			setBusy(false)
		}
	}

	const handleCopyHostedUrl = async () => {
		try {
			await navigator.clipboard.writeText(hostedFeed.publicUrl)
			setShowConnectionStep(false)
		} catch {
			setError('Не удалось скопировать ссылку. Скопируйте её вручную.')
		}
	}

	const handleRotateHostedUrl = async () => {
		const requestVersion = ++hostedFeedRequestVersion.current
		setBusy(true)
		setError('')
		const previousUrl = 'previous URL'
		try {
			const nextHostedFeed = await rotateAvitoHostedFeedToken(accountId)
			if (requestVersion !== hostedFeedRequestVersion.current) return
			setHostedFeed(nextHostedFeed)
			setOldUrlWarning(
				`Старая ссылка отключена. Замените её в настройках Авито: ${previousUrl}`
			)
		} catch (requestError) {
			setError(getAvitoHostedFeedErrorMessage(requestError))
		} finally {
			setBusy(false)
		}
	}

	const handleLoadDiagnostics = async () => {
		setBusy(true)
		setError('')
		try {
			const [preview, diagnostics] = await Promise.all([
				previewAvitoAutoloadFeed(accountId),
				hostedFeed?.configured
					? getAvitoHostedFeedDiagnostics(accountId)
					: Promise.resolve(null),
			])
			setFeedPreview(preview)
			setHostedDiagnostics(diagnostics)
		} catch (requestError) {
			setError(getAvitoHostedFeedErrorMessage(requestError))
		} finally {
			setBusy(false)
		}
	}

	const handleDownload = async () => {
		setBusy(true)
		setError('')
		try {
			saveAvitoFeedResponse(await fetchAvitoAutoloadFeed(accountId))
		} catch (requestError) {
			const invalidFeed = requestError?.response?.data
			if (invalidFeed?.valid === false) setFeedPreview(invalidFeed)
			setError(getAvitoAutoloadErrorMessage(requestError))
		} finally {
			setBusy(false)
		}
	}

	const handleExclude = async (listing) => {
		const confirmed = window.confirm(
			`Убрать «${listing.object?.label || `Объект ${listing.objectId}`}» из автозагрузки? Сам объект останется в Reldo.`
		)
		if (!confirmed) return
		setBusy(true)
		setError('')
		try {
			await excludeFromAvitoAutoload(listing.id)
			setShowConnectionStep(false)
			await loadAutoload()
		} catch (requestError) {
			setError(getAvitoAutoloadErrorMessage(requestError))
		} finally {
			setBusy(false)
		}
	}

	const openListingEditor = (listing) => {
		if (!listing?.objectId) return
		window.open(
			`/objects?editObjectId=${encodeURIComponent(listing.objectId)}`,
			'_blank',
			'noopener,noreferrer'
		)
	}

	const handleViewAction = (actionId) => {
		switch (actionId) {
			case 'add-account':
			case 'manage-account':
				onManageAccounts?.()
				break
			case 'add-listings':
				setAddOpen(true)
				break
			case 'fix-first-listing':
			case 'view-problem':
				openListingEditor(viewModel.invalidListings[0])
				break
			case 'prepare':
			case 'retry':
				handlePrepareOrRetry()
				break
			case 'copy-url':
				handleCopyHostedUrl()
				break
			case 'open-guide':
				setGuideOpen(true)
				break
			case 'open-settings':
				setSettingsOpen(true)
				break
			default:
				break
		}
	}

	if (loading && accounts.length === 0) {
		return (
			<Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
				<CircularProgress aria-label="Загрузка автозагрузки Авито" />
			</Box>
		)
	}

	return (
		<Stack spacing={3} sx={{ maxWidth: 1040 }}>
			<Box>
				<Typography variant="h5" gutterBottom>
					Автозагрузка Авито
				</Typography>
				<Typography color="text.secondary">
					Выберите объявления — Reldo проверит и будет поддерживать данные для
					Авито автоматически.
				</Typography>
			</Box>

			{accounts.length === 1 && (
				<Typography color="text.secondary">
					Аккаунт: {selectedAccount?.name}
				</Typography>
			)}
			{accounts.length > 1 && (
				<FormControl sx={{ maxWidth: 420 }} fullWidth>
					<InputLabel id="avito-autoload-account-label">Аккаунт Авито</InputLabel>
					<Select
						labelId="avito-autoload-account-label"
						label="Аккаунт Авито"
						value={accountId}
						onChange={(event) => setAccountId(event.target.value)}>
						{accounts.map((account) => (
							<MenuItem key={account.id} value={account.id}>
								{account.name}
								{account.status === 'disabled' ? ' — отключён' : ''}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			)}

			{error && <Alert severity="error">{error}</Alert>}
			<Card
				variant="outlined"
				role="status"
				aria-live="polite"
				sx={{ borderLeft: 4, borderLeftColor: `${viewModel.severity}.main` }}>
				<CardContent>
					<Stack spacing={1}>
						<Typography variant="h6">{viewModel.headline}</Typography>
						<Typography color="text.secondary">
							{viewModel.explanation}
						</Typography>
						{hostedFeed?.lastSuccessfulBuildAt &&
							viewModel.state === 'CONNECTED_CLEAN' && (
								<Typography variant="caption" color="text.secondary">
									Последнее обновление:{' '}
									{formatDateTime(hostedFeed.lastSuccessfulBuildAt)}
								</Typography>
							)}
					</Stack>
				</CardContent>
				{(viewModel.primaryAction || statusSecondaryAction) && (
					<CardActions sx={{ flexWrap: 'wrap', gap: 1 }}>
						{viewModel.primaryAction && (
							<Button
								variant="contained"
								onClick={() => handleViewAction(viewModel.primaryAction.id)}
								disabled={busy}>
								{viewModel.primaryAction.label}
							</Button>
						)}
						{statusSecondaryAction && (
							<Button
								onClick={() => handleViewAction(statusSecondaryAction.id)}
								disabled={busy}>
								{statusSecondaryAction.label}
							</Button>
						)}
					</CardActions>
				)}
			</Card>

			{accounts.length > 0 && (
				<Box>
					<Typography variant="h6" gutterBottom>
						Объявления в автозагрузке
					</Typography>
					{loading ? (
						<Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
							<CircularProgress aria-label="Обновление списка объявлений" />
						</Box>
					) : autoload.listings.length === 0 ? (
						<Typography color="text.secondary">
							Здесь появятся выбранные объявления.
						</Typography>
					) : (
						<Box
							sx={{
								display: 'grid',
								gridTemplateColumns: {
									xs: '1fr',
									md: 'repeat(2, minmax(0, 1fr))',
								},
								gap: 2,
							}}>
							{autoload.listings.map((listing) => {
								const ready = listing.health === 'READY'
								return (
									<Card key={listing.id} variant="outlined">
										<CardContent>
											<Stack spacing={1}>
												<Typography variant="h6">
													{listing.object?.label || `Объект ${listing.objectId}`}
												</Typography>
												{listing.object?.address &&
													listing.object.address !== listing.object.label && (
														<Typography color="text.secondary">
															{listing.object.address}
														</Typography>
													)}
												<Typography
													color={ready ? 'success.main' : 'warning.main'}
													fontWeight={600}>
													{ready
														? '✓ Готово'
														: `⚠ ${firstUserDiagnosticMessage(listing.diagnostics)}`}
												</Typography>
											</Stack>
										</CardContent>
										<CardActions sx={{ flexWrap: 'wrap' }}>
											{!ready && (
												<Button onClick={() => openListingEditor(listing)}>
													Исправить
												</Button>
											)}
											<Button
												color="error"
												onClick={() => handleExclude(listing)}
												disabled={busy || selectedAccount?.status !== 'active'}>
												Убрать из автозагрузки
											</Button>
										</CardActions>
									</Card>
								)
							})}
						</Box>
					)}
				</Box>
			)}

			{accounts.length > 0 && (
				<Button
					onClick={() => setSettingsOpen(true)}
					sx={{ alignSelf: 'flex-start' }}>
					Настройки автозагрузки ›
				</Button>
			)}

			<AvitoAutoloadAddDialog
				open={addOpen}
				account={selectedAccount}
				onClose={() => setAddOpen(false)}
				onIncluded={async () => {
					setAddOpen(false)
					setShowConnectionStep(true)
					await loadAutoload()
				}}
			/>
			<AvitoAutoloadSettingsDialog
				open={settingsOpen}
				onClose={() => setSettingsOpen(false)}
				hostedFeed={hostedFeed}
				busy={busy}
				diagnostics={hostedDiagnostics}
				feedPreview={feedPreview}
				oldUrlWarning={oldUrlWarning}
				onCopy={handleCopyHostedUrl}
				onOpenGuide={() => setGuideOpen(true)}
				onOpenXml={() =>
					window.open(hostedFeed.publicUrl, '_blank', 'noopener,noreferrer')
				}
				onDownload={handleDownload}
				onLoadDiagnostics={handleLoadDiagnostics}
				onRotate={handleRotateHostedUrl}
			/>
			<AvitoAutoloadGuideDialog
				open={guideOpen}
				onClose={() => setGuideOpen(false)}
			/>
		</Stack>
	)
}

export default AvitoAutoloadWorkspace
